import { ensureRoleScreens, isScreenAllowed } from '@/auth/role-screen-access';
import { ensureAppRegistry, isTileProvisioned } from '@/apps/use-app-tiles';

/**
 * Popup MFE access gate — parity with the legacy `BaseRouteGuard` popup check
 * (`validateRequestedURLAccessAndNavigate(secondaryUrl, popUrlCheck=true)` →
 * `isClientAndScreenValidForUser`). A popup opened by direct URL (`?popup=…`) or
 * an imperative call must be present in the current client's role mapping and
 * provisioned as a tile — the same two-layer gate applied by the header icon
 * (`use-header-icon-visibility`). Header buttons are already visibility-gated;
 * this gate closes the direct-URL and imperative bypass, so a request such as
 * `?popup=orchestratoruitasks` for a client without access no longer opens the
 * overlay (previously it mounted because the popup path validated only the
 * static `isPopupMfe` id list).
 *
 * The popup screen name mirrors the legacy `HeaderAppsEnums` / `tileRoutePath`
 * form `popup:apps/{appId}`, matched case-insensitively by both layers.
 */
export async function ensurePopupAccess(appId: string, clientId: string): Promise<boolean> {
  if (!appId || !clientId) return false;
  const screenPath = `popup:apps/${appId}`;

  let screens: readonly string[];
  try {
    screens = await ensureRoleScreens(clientId);
  } catch {
    // Role mapping unavailable — deny access. Legacy `isClientAndScreenValidForUser`
    // likewise resolves to false when the mapping request errors or returns no
    // screens.
    return false;
  }

  // Layer 1 — role-screen mapping. `isScreenAllowed` exempts essential and
  // allowlisted MFEs, matching the legacy `ALLOW_MFE_IDS` short-circuit.
  if (!isScreenAllowed(screenPath, screens)) return false;

  // Layer 2 — tile provisioning (legacy `isTileExistsInClient`). Warm the
  // client's tile feed first so the existence check is authoritative: an
  // unloaded feed degrades to "allow", so skipping this step could grant access
  // incorrectly.
  await ensureAppRegistry(clientId);
  return isTileProvisioned(clientId, screenPath);
}
